import axios from 'axios';
import { CONFIG } from '../config/constants';

// Define API base URL from constants
const API_URL = CONFIG.API.API_URL;

// Define types
export interface DocumentUploadResponse {
  success: boolean;
  message: string;
  document?: {
    id: number;
    name: string;
    file_path: string;
    file_type: string;
    processing_status: string;
    error_message?: string;
    created_at: string;
  };
  // Validation errors
  validation_failed?: boolean;
  errors?: {
    [key: string]: {
      message: string;
      details: string;
      requirements?: any;
    } | string;
  };
}

export interface Document {
  id: number;
  name: string;
  file_path: string;
  file_type: string;
  processed: boolean;
  processing_status: string;
  error_message?: string;
  created_at: string;
  download_url: string;
  ai_document_id?: string;
}

export interface DocumentListResponse {
  success: boolean;
  documents: Document[];
}

export interface DocumentResponse {
  success: boolean;
  document: Document;
}

export interface DeleteResponse {
  success: boolean;
  message: string;
}

// Supported file types
export const SUPPORTED_FILE_TYPES = [
  // Văn bản
  'application/pdf', // PDF
  'application/msword', // DOC
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
  'text/plain', // TXT

  // Hình ảnh
  'image/jpeg', // JPG, JPEG
  'image/png',  // PNG
  'image/tiff', // TIFF, TIF
  'image/tif',  // TIFF alternative
  'application/tiff', // TIFF alternative
  'image/bmp',  // BMP
  'image/webp', // WEBP
];

// Configure axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Accept': 'application/json',
  },
});

// Add a request interceptor to include the token in requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Document service
const documentService = {
  // Upload document
  uploadDocument: async (file: File, options?: {
    numQuestions?: number;
    difficulty?: string;
    language?: string;
    question_types?: { multiple_choice: number; true_false: number; fill_in_blank: number; }
  }): Promise<DocumentUploadResponse> => {
    try {
      // Create form data
      const formData = new FormData();
      formData.append('document', file);

      // Add optional parameters
      if (options?.numQuestions) {
        formData.append('num_questions', options.numQuestions.toString());
      }
      if (options?.difficulty) {
        formData.append('difficulty', options.difficulty);
      }
      if (options?.language) {
        formData.append('language', options.language);
      }
      if (options?.question_types) {
        formData.append('question_types[multiple_choice]', options.question_types.multiple_choice.toString());
        formData.append('question_types[true_false]', options.question_types.true_false.toString());
        formData.append('question_types[fill_in_blank]', options.question_types.fill_in_blank.toString());
      }

      // Set content type to multipart/form-data for file upload
      const response = await apiClient.post('/documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Tải lên tài liệu thất bại');
    }
  },

  // Upload multiple documents
  uploadMultipleDocuments: async (files: File[], options?: {
    numQuestions?: number;
    difficulty?: string;
    language?: string;
    question_types?: { multiple_choice: number; true_false: number; fill_in_blank: number; }
  }): Promise<DocumentUploadResponse> => {
    try {
      // Create form data
      const formData = new FormData();

      // Append all files
      files.forEach((file, index) => {
        formData.append(`documents[${index}]`, file);
      });

      // Add optional parameters
      if (options?.numQuestions) {
        formData.append('num_questions', options.numQuestions.toString());
      }
      if (options?.difficulty) {
        formData.append('difficulty', options.difficulty);
      }
      if (options?.language) {
        formData.append('language', options.language);
      }
      if (options?.question_types) {
        formData.append('question_types[multiple_choice]', options.question_types.multiple_choice.toString());
        formData.append('question_types[true_false]', options.question_types.true_false.toString());
        formData.append('question_types[fill_in_blank]', options.question_types.fill_in_blank.toString());
      }

      // Set content type to multipart/form-data for file upload
      const response = await apiClient.post('/documents/multiple', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Tải lên nhiều tài liệu thất bại');
    }
  },

  // Get all documents
  getDocuments: async (): Promise<DocumentListResponse> => {
    try {
      const response = await apiClient.get('/documents');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể lấy danh sách tài liệu');
    }
  },

  // Get document by ID
  getDocumentById: async (id: number): Promise<DocumentResponse> => {
    try {
      const response = await apiClient.get(`/documents/${id}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể lấy thông tin tài liệu');
    }
  },

  // Delete document
  deleteDocument: async (id: number): Promise<DeleteResponse> => {
    try {
      const response = await apiClient.delete(`/documents/${id}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể xóa tài liệu');
    }
  },

  // Process document with AI service
  processDocument: async (id: number, numQuestions: number = 10): Promise<any> => {
    try {
      const response = await apiClient.post(`/documents/${id}/process-async`, {
        num_questions: numQuestions
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể xử lý tài liệu');
    }
  },

  // Check document processing status
  checkProcessingStatus: async (id: number): Promise<any> => {
    try {
      const response = await apiClient.get(`/documents/${id}/status`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể kiểm tra trạng thái xử lý');
    }
  },

  // Check document upload status for polling
  checkUploadStatus: async (id: number): Promise<any> => {
    try {
      const response = await apiClient.get(`/documents/${id}/check-status-upload`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể kiểm tra trạng thái upload');
    }
  },

  // Poll document processing status until completion
  pollProcessingStatus: async (id: number, maxAttempts: number = 30, interval: number = 2000): Promise<any> => {
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const response = await documentService.checkProcessingStatus(id);

        // If processing is complete or failed, return the result
        if (response.processing_status === 'completed' || response.processing_status === 'failed') {
          return response;
        }

        // Wait before next attempt
        await new Promise(resolve => setTimeout(resolve, interval));
        attempts++;

      } catch (error) {
        console.error('Error polling status:', error);
        attempts++;
        await new Promise(resolve => setTimeout(resolve, interval));
      }
    }

    throw new Error('Hết thời gian chờ - đã đạt số lần thử tối đa');
  },

  // Poll document upload status until completion
  pollUploadStatus: async (
    id: number,
    onStatusUpdate?: (status: string, data: any) => void,
    maxAttempts: number = 60,
    interval: number = 2000
  ): Promise<any> => {
    let attempts = 0;

    while (attempts < maxAttempts) {
      try {
        const response = await documentService.checkUploadStatus(id);

        // Call status update callback if provided
        if (onStatusUpdate) {
          onStatusUpdate(response.processing_status, response);
        }

        // If processing is complete or failed, return the result
        if (response.processing_status === 'completed' ||
          response.processing_status === 'upload_failed' ||
          response.processing_status === 'processing_failed') {
          return response;
        }

        // Wait before next attempt
        await new Promise(resolve => setTimeout(resolve, interval));
        attempts++;

      } catch (error) {
        console.error('Error polling upload status:', error);
        attempts++;
        await new Promise(resolve => setTimeout(resolve, interval));
      }
    }

    throw new Error('Hết thời gian upload - đã đạt số lần thử tối đa');
  },

  // Get document questions
  getDocumentQuestions: async (id: number): Promise<any> => {
    try {
      const response = await apiClient.get(`/documents/${id}/questions`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw new Error('Không thể lấy danh sách câu hỏi của tài liệu');
    }
  },

  // Validate file type
  validateFileType: (file: File): boolean => {
    // First check MIME type
    if (SUPPORTED_FILE_TYPES.includes(file.type)) {
      return true;
    }

    // Fallback: check file extension for TIFF files
    const fileName = file.name.toLowerCase();
    const supportedExtensions = [
      '.pdf', '.doc', '.docx', '.txt',
      '.jpg', '.jpeg', '.png', '.tiff', '.tif', '.bmp', '.webp'
    ];

    return supportedExtensions.some(ext => fileName.endsWith(ext));
  },

  // Get file type icon
  getFileTypeIcon: (fileType: string): string => {
    if (fileType.includes('pdf')) {
      return 'PictureAsPdf';
    } else if (fileType.includes('word') || fileType.includes('doc')) {
      return 'Article';
    } else if (fileType.includes('text') || fileType.includes('txt')) {
      return 'TextSnippet';
    }
    return 'InsertDriveFile';
  }
};

export default documentService; 